import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PreviewModeProvider, usePreviewMode } from './contexts/PreviewModeContext';
import ErrorBoundary from './ErrorBoundary';
import Header from './components/Header/Header.jsx';
import Footer from './components/Footer/Footer';
import AuthModal from './components/AuthModal/AuthModal';
import HeroBanner from './components/HeroBanner/HeroBanner';
import BrowseProducts from './components/BrowseProducts/BrowseProd';
import VisitUs from './components/VisitUs/VisitUs';
import Features from './components/Features/Features';
import Collections from './components/Collections/Collections';
import Promotions from './components/Promotions/Promotions';
import ProductCatalog from './components/ProductCatalog/ProductCatalog';
import ProductDetails from './components/ProductDetails/ProductDetails';
import CollectionsPage from './components/CollectionsPage/CollectionsPage';
import ChatInquiry from './components/ChatInquiry/ChatInquiry';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import AdminRoute from './components/AdminRoute/AdminRoute';
import AdminLayout from './components/AdminLayout/AdminLayout';
import OrderList from './components/AdminOrderList/AdminOrderList.jsx';
import AdminOrderDetails from './components/AdminOrderDetails/AdminOrderDetails.jsx';
import AdminReports from './components/AdminReports/AdminReports';
import AdminSettings from './components/AdminSettings/AdminSettings';
import AdminPromotions from './components/AdminPromotions/AdminPromotions';
import AdminPromotionDetails from './components/AdminPromotionDetails/AdminPromotionDetails';
import AdminDashboard from './components/AdminDashboard/AdminDashboard';
import Breadcrumb from './components/Breadcrumb/Breadcrumb';
import HeroVideo from './components/HeroVideo/HeroVideo';
import AdminCollections from './components/AdminCollections/AdminCollections';
import PreviewBanner from './components/AdminPreviewMode/PreviewBanner';
import Collab from './components/Collab/Collab';
import Feature from './components/FeaturedClients/FeaturedClients.jsx';
import { getDefaultAdminRoute, isAdminPanelRole } from './utils/adminRoles';
import {
    AdminProductsPage,
    AdminCategoriesPage,
    AdminSubcategoriesPage,
    AdminSubmenusPage,
    AdminAttributesPage,
    AdminInventoryPage,
    AdminChatsPage
} from './components/AdminPages/AdminPages';

// Cart & Checkout components
import Cart from './components/Cart/Cart';
import OrderReview from './components/OrderReview/OrderReview';
import OrderApproval from './components/OrderApproval/OrderApproval';
import Checkout from './components/Checkout/Checkout';
import OrderConfirmation from './components/OrderConfirmation/OrderConfirmation';

// Account layout + pages
import AccountLayout from './components/AccountLayout/AccountLayout';
import PersonalInformation from './components/PersonalInformation/PersonalInformation';
import SavedAddresses from './components/SavedAddresses/SavedAddresses';
import PurchaseHistory from './components/PurchaseHistory/PurchaseHistory';
import EmailNotifications from './components/EmailNotifications/EmailNotifications';

// Design Editor
import DesignEditor from './components/DesignEditor/DesignEditor';

// Footer pages
import FAQs from './components/FAQs/FAQs';
import AboutUs from './components/AboutUs/AboutUs';
import HowToOrder from './components/HowToOrder/HowToOrder';
import PrivacyPolicy from './components/PrivacyPolicy/PrivacyPolicy';
import TermsAndConditions from './components/TermsAndConditions/TermsAndConditions';

import './App.css';

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
}

// ── Preview-mode blocked page ─────────────────────────────────────────────────
function PreviewBlocked() {
    return (
        <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', minHeight: '60vh', gap: '16px',
            textAlign: 'center', padding: '40px', backgroundColor: '#ffffff'
        }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10"/>
                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
            </svg>
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>
                Not Available in Preview Mode
            </h2>
            <p style={{ fontSize: '14px', color: '#888', maxWidth: '380px', lineHeight: '1.6', margin: 0 }}>
                This page requires a user account and is disabled during Admin Preview Mode.
            </p>
        </div>
    );
}

// ── Route guard for preview mode ──────────────────────────────────────────────
function PreviewGuard({ children }) {
    const isPreview = usePreviewMode();
    if (isPreview) return <PreviewBlocked />;
    return children;
}

// ── HomePage ─────────────────────────────────────────────────────────────────
// Layout order (matches WizardZ reference design):
//  1. HeroVideo    — left: JG Printing brand + Shop Now CTA | right: YouTube embed
//  2. HeroBanner   — promotional banner slider (full width)
//  3. Features     — 3 feature cards (Reliable, Affordable, Versatile)
//  4. BrowseProducts — product carousel
//  5. Collections  — 6-card grid (3 cols × 2 rows)
//  6. Features     — large rectangle feature section
//  7. Collab       — large rectangle collab section
//  8. VisitUs      — marketplace cards (Lazada, Shopee, TikTok)
//  9. FeaturedClients — client logos / cards
function HomePage() {
    const { currentUser, userRole, loading } = useAuth();
    const isPreview = usePreviewMode();
    const navigate = useNavigate();
    const [pageLoading, setPageLoading] = useState(true);

    useEffect(() => {
        if (!loading && currentUser && isAdminPanelRole(userRole) && !isPreview) {
            navigate('/admin', { replace: true });
        }
    }, [currentUser, userRole, loading, navigate, isPreview]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setPageLoading(false);
        }, 1000);
        return () => clearTimeout(timer);
    }, []);

    if (pageLoading) {
        return (
            <div className="global-loader">
                <div className="loader-wrap">
                    <div className="loader"></div>
                    <p className="loader-text">Loading....</p>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* 1. Hero — brand left, YouTube right */}
            <HeroVideo />

            {/* 2. Promotional banner slider */}
            <HeroBanner />

            {/* 3. Feature pills (Reliable / Affordable / Versatile) */}
            <Features />

            {/* 4. Browse Products carousel */}
            <BrowseProducts />

            {/* 5. Collections — 6 cards, 3 × 2 grid */}
            <Collections />

            {/* 6. Featured Clients — wide rectangle section */}
            <Feature />

            {/* 7. Collaborations — wide rectangle section */}
            <Collab />

            {/* 8. Visit Us — marketplace cards */}
            <VisitUs />
        </>
    );
}

function AppContent() {
    const { currentUser } = useAuth();
    const isPreview = usePreviewMode();
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const location = useLocation();
    const isAdminRoute = location.pathname.startsWith('/admin');

    const handleOpenAuthModal = () => setIsAuthModalOpen(true);
    const handleCloseAuthModal = () => setIsAuthModalOpen(false);

    const handleOpenChat = () => {
        if (isPreview) return;
        if (!currentUser) { setIsAuthModalOpen(true); return; }
        setIsChatOpen(true);
    };

    const handleCloseChat = () => setIsChatOpen(false);

    function AdminIndexRedirect() {
        const { userRole } = useAuth();
        return <Navigate to={getDefaultAdminRoute(userRole)} replace />;
    }

    return (
        <div className="app">

            {!isAdminRoute && <PreviewBanner />}

            {!isAdminRoute && (
                <Header
                    onLoginClick={handleOpenAuthModal}
                    onMessageClick={handleOpenChat}
                />
            )}

            {!isAdminRoute && <Breadcrumb />}

            <main className={`main-content${isAdminRoute ? ' admin-main-content' : ''}`}>
                <Routes>
                    {/* ── Home ── */}
                    <Route path="/" element={<HomePage />} />

                    {/* ── Shop ── */}
                    <Route path="/catalog" element={<ProductCatalog />} />
                    <Route path="/clothing" element={<ProductCatalog />} />
                    <Route path="/promotions" element={<Promotions />} />
                    <Route path="/product-details" element={<ProductDetails />} />
                    <Route path="/design-editor" element={<DesignEditor />} />

                    {/* ── Cart & Checkout ── */}
                    <Route path="/cart"                     element={<PreviewGuard><Cart /></PreviewGuard>} />
                    <Route path="/order-review"             element={<PreviewGuard><OrderReview /></PreviewGuard>} />
                    <Route path="/order-approval-submitted" element={<PreviewGuard><OrderApproval /></PreviewGuard>} />
                    <Route path="/checkout"                 element={<PreviewGuard><Checkout /></PreviewGuard>} />
                    <Route path="/order-confirmation"       element={<PreviewGuard><OrderConfirmation /></PreviewGuard>} />

                    {/* ── Account ── */}
                    <Route
                        path="/account"
                        element={
                            isPreview ? <PreviewBlocked /> :
                            <ProtectedRoute><AccountLayout /></ProtectedRoute>
                        }
                    >
                        <Route index element={<Navigate to="personal-information" replace />} />
                        <Route path="personal-information" element={<PersonalInformation />} />
                        <Route path="saved-address"        element={<SavedAddresses />} />
                        <Route path="purchase-history"     element={<PurchaseHistory />} />
                        <Route path="email-notifications"  element={<EmailNotifications />} />
                    </Route>

                    {/* ── Admin ── */}
                    <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                        <Route index element={<AdminIndexRedirect />} />
                        <Route path="dashboard"            element={<AdminRoute requiredPath="/admin/dashboard"><AdminDashboard /></AdminRoute>} />
                        <Route path="products"             element={<AdminRoute requiredPath="/admin/products/manage"><Navigate to="/admin/products/manage" replace /></AdminRoute>} />
                        <Route path="products/manage"      element={<AdminRoute requiredPath="/admin/products/manage"><AdminProductsPage /></AdminRoute>} />
                        <Route path="products/categories"  element={<AdminRoute requiredPath="/admin/products/categories"><AdminCategoriesPage /></AdminRoute>} />
                        <Route path="products/subcategories" element={<AdminRoute requiredPath="/admin/products/subcategories"><AdminSubcategoriesPage /></AdminRoute>} />
                        <Route path="products/submenus"    element={<AdminRoute requiredPath="/admin/products/submenus"><AdminSubmenusPage /></AdminRoute>} />
                        <Route path="products/attributes"  element={<AdminRoute requiredPath="/admin/products/attributes"><AdminAttributesPage /></AdminRoute>} />
                        <Route path="products/inventory"   element={<AdminRoute requiredPath="/admin/products/inventory"><AdminInventoryPage /></AdminRoute>} />
                        <Route path="orders"               element={<AdminRoute requiredPath="/admin/orders/list"><Navigate to="/admin/orders/list" replace /></AdminRoute>} />
                        <Route path="orders/list"          element={<AdminRoute requiredPath="/admin/orders/list"><OrderList /></AdminRoute>} />
                        <Route path="orders/details"       element={<AdminRoute requiredPath="/admin/orders/details"><AdminOrderDetails /></AdminRoute>} />
                        <Route path="promotions"           element={<AdminRoute requiredPath="/admin/promotions/manage"><Navigate to="/admin/promotions/manage" replace /></AdminRoute>} />
                        <Route path="promotions/manage"    element={<AdminRoute requiredPath="/admin/promotions/manage"><AdminPromotions /></AdminRoute>} />
                        <Route path="promotions/details"   element={<AdminRoute requiredPath="/admin/promotions/details"><AdminPromotionDetails /></AdminRoute>} />
                        <Route path="collections"          element={<AdminRoute requiredPath="/admin/collections"><AdminCollections /></AdminRoute>} />
                        <Route path="chats"                element={<AdminRoute requiredPath="/admin/chats"><AdminChatsPage /></AdminRoute>} />
                        <Route path="reports"              element={<AdminRoute requiredPath="/admin/reports/sales"><Navigate to="/admin/reports/sales" replace /></AdminRoute>} />
                        <Route path="reports/*"            element={<AdminRoute requiredPath="/admin/reports/sales"><AdminReports /></AdminRoute>} />
                        <Route path="settings"             element={<AdminRoute requiredPath="/admin/settings/account"><AdminSettings /></AdminRoute>} />
                        <Route path="settings/*"           element={<AdminRoute requiredPath="/admin/settings/account"><AdminSettings /></AdminRoute>} />
                    </Route>

                    {/* ── Placeholder routes ── */}
                    <Route path="/all-products"           element={<div style={{ padding: '60px 40px', textAlign: 'center', backgroundColor: '#ffffff', minHeight: '60vh' }}><h1>All Products</h1><p>Coming soon...</p></div>} />
                    <Route path="/accessories"            element={<div style={{ padding: '60px 40px', textAlign: 'center', backgroundColor: '#ffffff', minHeight: '60vh' }}><h1>Accessories</h1><p>Coming soon...</p></div>} />
                    <Route path="/printed-materials"      element={<div style={{ padding: '60px 40px', textAlign: 'center', backgroundColor: '#ffffff', minHeight: '60vh' }}><h1>Printed Materials</h1><p>Coming soon...</p></div>} />
                    <Route path="/promotional-materials"  element={<div style={{ padding: '60px 40px', textAlign: 'center', backgroundColor: '#ffffff', minHeight: '60vh' }}><h1>Promotional Materials</h1><p>Coming soon...</p></div>} />
                    <Route path="/packaging-display"      element={<div style={{ padding: '60px 40px', textAlign: 'center', backgroundColor: '#ffffff', minHeight: '60vh' }}><h1>Packaging &amp; Display</h1><p>Coming soon...</p></div>} />
                    <Route path="/full-sublimation"       element={<div style={{ padding: '60px 40px', textAlign: 'center', backgroundColor: '#ffffff', minHeight: '60vh' }}><h1>Full Sublimation</h1><p>Coming soon...</p></div>} />

                    {/* ── Footer Pages ── */}
                    <Route path="/faqs"                  element={<FAQs />} />
                    <Route path="/about-us"              element={<AboutUs />} />
                    <Route path="/how-to-order"          element={<HowToOrder />} />
                    <Route path="/terms-and-conditions"  element={<TermsAndConditions />} />
                    <Route path="/privacy-policy"        element={<PrivacyPolicy />} />

                    {/* ── Collections ── */}
                    <Route path="/collections" element={<CollectionsPage />} />

                    {/* ── 404 ── */}
                    <Route path="*" element={
                        <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                            <h1>404 - Page Not Found</h1>
                            <p>The page you're looking for doesn't exist.</p>
                        </div>
                    } />
                </Routes>
            </main>

            {!isAdminRoute && <Footer />}

            {!isAdminRoute && !isPreview && (
                <AuthModal isOpen={isAuthModalOpen} onClose={handleCloseAuthModal} />
            )}

            {!isAdminRoute && !isPreview && (
                <ChatInquiry isOpen={isChatOpen} onClose={handleCloseChat} />
            )}
        </div>
    );
}

function AppWithRouter() {
    return (
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <ScrollToTop />
            <PreviewModeProvider>
                <ErrorBoundary>
                    <AppContent />
                </ErrorBoundary>
            </PreviewModeProvider>
        </Router>
    );
}

function App() {
    return (
        <AuthProvider>
            <AppWithRouter />
        </AuthProvider>
    );
}

export default App;